import type { MortalityWindow, SectionAlarm, SectionLiveKpi } from '@/types/SectionLiveKpi';
import type { AttentionLevel, SectionSignals } from './needsAttention';

// A single Section's view-model for the Active groups table. Built in the page (Index.vue)
// by merging production-groups/barns data with the dedicated live-KPI endpoint, then rendered as a table row.
export type SectionRow = {
    id: number;
    sectionName: string;
    barnName: string;
    farmName: string;
    farmId: number;
    liveGrowthIndex: number | null;
    dailyGain: number | null;
    day: number | null;
    week: number | null;
    // The Section's own head count. Production Group-independent — populated for inactive rows too, so
    // the barn header's "Pigs in barn" total reconciles against the rows beneath it.
    pigCount: number | null;
    latestWeightKg: number | null;
    // Weight source (estimated vs measured); drives the muted/italic styling of the weight value.
    weightSource: 'Estimated' | 'Measured' | null;
    // How many of the Section's cameras each weight figure is a mean over, and how many are mounted now.
    // Each contributing count is null whenever its own figure is; expected is null only on a row the
    // live-KPI read states nothing for. Strictly fewer contributing than expected means that figure
    // covers part of the Section, which the cell marks rather than blanking - see DegradedCameraMarker.
    //
    // Three counts because the denominators differ: a camera's newest reading holds a weight far more
    // often than a growth, so mark each cell from its own count. Wire fields:
    // `weight.contributing_weight_camera_count` and its two siblings / `weight.expected_camera_count`.
    contributingWeightCameraCount: number | null;
    contributingDailyGainCameraCount: number | null;
    contributingGrowthIndexCameraCount: number | null;
    expectedCameraCount: number | null;

    // Now, on the Directory's own wall clock, offset-free - the scale this page's series buckets are
    // stated on. Every chart axis here that ends at "now" reads it (see directoryLocalNow.ts); a
    // browser-computed edge lands a whole UTC offset away from the buckets drawn against it. Null only
    // on a row the live-KPI read states nothing for. Wire field: `directory_local_now`.
    directoryLocalNow: string | null;
    // When each of the three weight figures was observed (ISO instant, UTC) - one per figure, each null
    // whenever its figure is. Stated in the weight detail modal because the charts there bucket by a fixed
    // hour of the directory's day while these are latest-known readings, so a card and the chart's last
    // point legitimately differ. Three rather than one because each figure is read at its own latest row,
    // typically the gain and index trailing the weight. Wire fields: `weight.weight_as_of_utc` /
    // `weight.daily_gain_as_of_utc` / `weight.growth_index_as_of_utc`.
    weightAsOfUtc: string | null;
    dailyGainAsOfUtc: string | null;
    growthIndexAsOfUtc: string | null;
    // Water consumption as a percentage of the Nexus Water Norm (actual ÷ Water Norm × 100); rendered
    // numerically, coloured by the unified index scheme. Wire field: `water.consumption_index_pct`.
    waterIndex: number | null;
    // Which day (yyyy-MM-dd) `waterIndex` was actually computed from (yesterday or two-days-ago —
    // the water-index logic falls back from yesterday when that reading is unstable/missing). Null
    // under the same conditions as `waterIndex` being null.
    waterAsOfDate: string | null;
    // Raw L/pig/day figures for the same day as `waterAsOfDate` — feed the water detail modal.
    waterActualWater: number | null;
    waterNormWater: number | null;
    // True when the Section holds no pigs (`pigCount` null or 0). Derived from `pigCount`, so it is
    // meaningful on inactive rows too.
    isEmpty: boolean;
    // True when an active Production Group exists for this physical Section. Sections without one render
    // as muted rows — their pig count plus a "Start Production Group" pill — pinned to the bottom of
    // their Barn group.
    hasProductionGroup: boolean;
    // Dynamic Mortality: rolling (min(7, days-since-Production Group-start) window) equivalent full-cycle
    // rate as a percentage (null → dash). Shown by default.
    mortalityPercent: number | null;
    // True when the rolling mortality rate is at or above target → percentage rendered red and card escalated.
    mortalityAtOrAboveTarget: boolean | null;
    // Dynamic Mortality: since-Production Group-start cumulative equivalent full-cycle rate as a
    // percentage (null → dash). Shown when the page-level toggle selects the cumulative view.
    mortalityCumulativePercent: number | null;
    // True when the cumulative mortality rate is at or above target → percentage rendered red and card escalated.
    mortalityCumulativeAtOrAboveTarget: boolean | null;
    // Days in the piggery used to normalise both mortality windows (null when the figures are suppressed).
    mortalityDaysInPiggery: number | null;
    // True → days in the piggery from the farm's slaughter data; false → per-animal-type standard.
    mortalityDaysInPiggeryDerived: boolean | null;
    // The section's mortality target as a percentage (null when no active target).
    mortalityTargetPercent: number | null;
    // Figures behind the rolling / cumulative rates — feed the mortality detail modal.
    mortalityRolling: MortalityWindow | null;
    mortalityCumulative: MortalityWindow | null;
    // Why the mortality percent(s) are null (backend-computed). Null when a figure is available.
    // Mirrors SectionLiveKpi.mortality.suppression_reason — see that field for what each value means.
    mortalitySuppressionReason: SectionLiveKpi['mortality']['suppression_reason'];
    // The active Production Group's id (null when none) — drives the manage modal's create vs edit mode.
    productionGroupId: number | null;
    // The active Production Group's name (null when no Production Group) — shown subtly next to the section name.
    productionGroupName: string | null;
    // The active Production Group's start date (ISO yyyy-MM-dd, null when none) — the detail overlay's
    // Productivity Chart defaults its range to this Production Group rather than recomputing
    // "start" backward from `day`, which would drift from the server's own resolution near a
    // Directory-local day boundary.
    productionGroupStart: string | null;
    // The owning Barn's id — always set, needed by the manage modal's create flow.
    barnId: number;
    // False when no weight sensor is assigned to this Section — weight, Daily Gain and Growth Index
    // will never populate; the frontend shows a "no sensor" icon instead of a dash.
    hasWeightSensor: boolean;
    // False when no water sensor is assigned to this Section — Water Index will never populate.
    hasWaterSensor: boolean;
    // Active (unresolved, within 48h) alarms on this Section. Empty when none.
    // Non-empty drives the red alarm chip in the Alder column and card lighting.
    activeAlarms: SectionAlarm[];
    // Precomputed once per row (useSectionRows) via `sectionSignals` + `needsAttention` (Module A) -
    // the single source of truth for both card colouring (Index.vue) and attention-first sorting
    // (sortSections.ts), so neither recomputes it.
    attentionLevel: AttentionLevel;
};

// Sections grouped under their Barn for the card grid.
export type BarnGroup = {
    barnName: string;
    barnId: number;
    sections: SectionRow[];
};

// The page-level mortality view: 'rolling' is the 7-day figure (default), 'cumulative' is the
// since-Production Group-start figure. Selected by a single toggle that applies to every row at once.
export type MortalityView = 'rolling' | 'cumulative';

// Select the mortality percentage for the currently active view. Rolling and cumulative are both
// carried on the row; the toggle decides which one every cell reads.
export function mortalityPercentForView(section: SectionRow, view: MortalityView): number | null {
    return view === 'cumulative' ? section.mortalityCumulativePercent : section.mortalityPercent;
}

// Select the Pigs to Target figure for the currently active view - mirrors mortalityPercentForView's
// rolling/cumulative split so the `(+N)`/`(-N)` indicator always agrees with the % it sits next to
// about which window it's showing. Read off the window object directly (not a dedicated SectionRow
// field) since it's already carried there for the detail modal.
export function mortalityPigsToTargetForView(section: Pick<SectionRow, 'mortalityRolling' | 'mortalityCumulative'>, view: MortalityView): number | null {
    const window = view === 'cumulative' ? section.mortalityCumulative : section.mortalityRolling;
    return window?.pigs_to_target ?? null;
}

// Select the at-or-above-target flag for the currently active view (drives the red vs green
// colouring of the mortality figure itself; it does not light the card). Typed on just the two
// fields it reads rather than the full SectionRow, so a caller holding only those can use it.
export function mortalityAtOrAboveTargetForView(
    section: Pick<SectionRow, 'mortalityAtOrAboveTarget' | 'mortalityCumulativeAtOrAboveTarget'>,
    view: MortalityView
): boolean | null {
    return view === 'cumulative' ? section.mortalityCumulativeAtOrAboveTarget : section.mortalityAtOrAboveTarget;
}

// Project a row onto the signals consumed by the pure `needsAttention` evaluator (Module A).
// Takes the row shape minus `attentionLevel` itself, since useSectionRows calls this before that
// field exists yet (it's what derives it).
export function sectionSignals(section: Omit<SectionRow, 'attentionLevel'>): SectionSignals {
    return {
        hasAlarms: section.activeAlarms.length > 0,
        isEmpty: section.isEmpty,
    };
}
